import { useState } from "react";
import { LOGO_URL } from "@/lib/useReveal";
import { Field, Input, Button } from "@/admin/ui";
import { userApi, USER_TOKEN_KEY, formatApiError, pathForRole } from "@/user/api";
import { useUserAuth } from "@/user/UserAuthContext";

export default function ChangePassword() {
    const { user, status, refresh } = useUserAuth();
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);

    if (status === "loading") {
        return (
            <div className="dot-grid min-h-screen flex items-center justify-center text-neutral-500">
                Loading…
            </div>
        );
    }
    if (status === "guest" || !localStorage.getItem(USER_TOKEN_KEY)) {
        if (typeof window !== "undefined") window.location.assign("/login");
        return null;
    }

    async function onSubmit(e) {
        e.preventDefault();
        setErr("");
        if (next !== confirm) {
            setErr("New password and confirmation do not match.");
            return;
        }
        setBusy(true);
        try {
            await userApi.post("/user/change-password", {
                current_password: current,
                new_password: next,
            });
            await refresh();
            const dest = pathForRole(user?.role) || "/app";
            window.location.assign(dest === "/app/ceo" ? "/app/chat" : dest);
        } catch (e2) {
            setErr(formatApiError(e2));
            setBusy(false);
        }
    }

    return (
        <div
            data-testid="change-password-page"
            className="dot-grid min-h-screen w-full relative flex items-center justify-center px-6 py-16"
        >
            <div
                className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[560px] w-[820px] max-w-[95vw] rounded-full opacity-60 anim-pulse-glow"
                style={{
                    background:
                        "radial-gradient(closest-side, rgba(139,92,246,0.3), rgba(139,92,246,0.05) 60%, transparent 75%)",
                }}
            />
            <div className="relative w-full max-w-md">
                <div className="flex justify-center">
                    <img
                        src={LOGO_URL}
                        alt="CEO's GPT"
                        className="logo-blend relative h-20 w-20 object-contain"
                        style={{ filter: "drop-shadow(0 20px 40px rgba(139,92,246,0.35))" }}
                    />
                </div>

                <h1 className="mt-6 text-center font-display text-4xl sm:text-5xl font-light tracking-tight text-black">
                    Set a new <span className="italic font-normal text-neutral-800">password</span>
                </h1>
                <p className="mt-3 text-center text-[14px] text-neutral-600">
                    For your security, please change the temporary password shared with you before continuing.
                </p>

                <form
                    onSubmit={onSubmit}
                    className="mt-8 glass-strong rounded-3xl p-6 sm:p-8 space-y-4"
                >
                    <Field label="Current / Temporary Password">
                        <Input
                            data-testid="cp-current-input"
                            type="password"
                            required
                            autoComplete="current-password"
                            value={current}
                            onChange={(e) => setCurrent(e.target.value)}
                            placeholder="••••••••"
                        />
                    </Field>
                    <Field label="New Password" hint="At least 8 characters, with a letter and a number.">
                        <Input
                            data-testid="cp-new-input"
                            type="password"
                            required
                            autoComplete="new-password"
                            value={next}
                            onChange={(e) => setNext(e.target.value)}
                            placeholder="••••••••"
                        />
                    </Field>
                    <Field label="Confirm New Password">
                        <Input
                            data-testid="cp-confirm-input"
                            type="password"
                            required
                            autoComplete="new-password"
                            value={confirm}
                            onChange={(e) => setConfirm(e.target.value)}
                            placeholder="••••••••"
                        />
                    </Field>

                    {err && (
                        <div
                            data-testid="cp-error"
                            className="rounded-xl border border-rose-200 bg-rose-50/70 text-rose-700 text-[13px] px-3.5 py-2.5"
                        >
                            {err}
                        </div>
                    )}

                    <Button
                        data-testid="cp-submit"
                        type="submit"
                        size="lg"
                        disabled={busy}
                        className="w-full justify-center"
                    >
                        {busy ? "Updating…" : "Update password & continue"}
                    </Button>
                </form>
            </div>
        </div>
    );
}
